import { Sparkle, Warning, Megaphone } from '@phosphor-icons/react';
import { IconButton } from '../IconButton';
import { cn } from 'shared/utils/cn';

export interface ToastProps {
  variant?: 'success' | 'error' | 'info';
  title: string;
  message: string;
  onClose?: () => void;
}

const variantConfig = {
  success: {
    borderColor: 'border-tertiary',
    iconBg: 'bg-tertiary-container/20',
    icon: <Sparkle size={20} weight="fill" className="text-tertiary" />,
  },
  error: {
    borderColor: 'border-error/40',
    iconBg: 'bg-error-container/20',
    icon: <Warning size={20} className="text-error" />,
  },
  info: {
    borderColor: 'border-primary/40',
    iconBg: 'bg-primary-container/30',
    icon: <Megaphone size={20} className="text-primary" />,
  },
};

export function Toast({ variant = 'success', title, message, onClose }: ToastProps) {
  const config = variantConfig[variant];

  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      aria-live={variant === 'error' ? 'assertive' : 'polite'}
      className={cn('bg-surface-container/60 backdrop-blur-xl p-5 rounded-xl border-l-4 shadow-2xl transition-transform duration-500 hover:-translate-x-2', config.borderColor)}
    >
      <div className="flex items-start gap-4">
        <div className={cn('p-2 rounded-full flex items-center justify-center', config.iconBg)}>
          {config.icon}
        </div>
        <div className="flex-1">
          <h4 className="font-serif text-on-surface text-lg font-medium">{title}</h4>
          <p className="font-sans text-on-surface-variant text-sm mt-1">{message}</p>
        </div>
        {onClose && (
          <IconButton
            variant="link"
            size="sm"
            icon="x"
            onClick={onClose}
            className="text-outline hover:text-on-surface"
            aria-label="Dismiss"
          />
        )}
      </div>
    </div>
  );
}
